import { bcStates } from 'common/common/states/';
import { bcEnums } from 'common/common/constants/boldchat-enums';
import { Url } from 'shared/services/utils/url.service';
import { RxEmitterInstance } from 'common/rxjs/rx-emitter';
import { MessageBusEvents } from 'common/common/constants/message-bus-events';
import { Injectable, Injector } from '@angular/core';
import { HttpErrorResponse, HttpHandler, HttpInterceptor, HttpRequest } from '@angular/common/http';
import { catchError } from 'rxjs/operators';
import { StateServiceWrapper } from 'shared/services/state-service-wrapper/state-service-wrapper';
import { NotificationBarService } from 'shared/services/notification/notification-bar.service';
import { BcAnalytics } from 'common/analytics/analytics.service';
import { WindowService } from 'shared/services/window/window.service';
import { LoggerService } from 'common/logger/logger.service';
import { LogService } from 'common/logger/log.service';
import { StatusCodes } from 'http-status-codes';
import { TELEMETRY_SERVICE } from 'common/telemetry';

@Injectable()
export class BcHttpInterceptor implements HttpInterceptor {
  private _logger: LoggerService;

  constructor(private _state: StateServiceWrapper,
              private _notificationService: NotificationBarService,
              private _bcAnalytics: BcAnalytics,
              private _window: WindowService,
              private _logService: LogService,
              private _injector: Injector
  ) {
    this._logger = this._logService.getLoggerInstance(BcHttpInterceptor.name);
  }

  intercept(request: HttpRequest<any>, next: HttpHandler) {
    let headers = request.headers;
    if (request.headers.has('X-Skip-Interceptor')) {
      headers = request.headers.delete('X-Skip-Interceptor');
    }
    return next.handle(request.clone({ headers })).pipe(
      catchError((error: any) => {
        if (!request.headers.has('X-Skip-Interceptor')) {
          this.handleResponseError(error);
        }

        throw error;
      })
    );
  }

  handleResponseError(error: HttpErrorResponse) {
    this._logger.logError('Received error {} from: {}', [error.status.toString(), error.url]);
    if (error.status === StatusCodes.UNAUTHORIZED && this.isAuthorizationNeeded() && !this.isIdentityValidationError(error)) {
      this._notificationService.addNotification({ id: 'logged-out', content: 'httpInterceptor.unauthorizedMessage' });
      RxEmitterInstance.send(MessageBusEvents.BcAuthLogout, null);
    } else if (error.status === StatusCodes.INTERNAL_SERVER_ERROR) {
      this._bcAnalytics.eventTrack('ServerError', { error });
      this.reportTelemetryError(error);
      this._notificationService.addNotification({ id: 'server-error', content: 'httpInterceptor.internalErrorMessage' });
    } else if (error.status === StatusCodes.FORBIDDEN && !(error.error && error.error[0] && this.handleRejection(error.error[0].code, error.error[0].details))) {
      this._notificationService.addNotification({ id: 'permission-error', content: 'httpInterceptor.forbiddenMessage' });
    } else {
      this.reportTelemetryError(error);
    }
  }

  private reportTelemetryError(error: HttpErrorResponse): void {
    // on-demand inject to break dependency circle
    const telemetryService = this._injector.get(TELEMETRY_SERVICE);
    telemetryService.reportError(error.message);
  }

  handleRejection(code: string, details: string): boolean {
    switch (code) {
      case bcEnums.loginErrorCodes.PRODUCT_MISMATCH:
        const domains = details.split(':');
        const newHost = this._window.location.hostname.replace(domains[0], domains[1]);
        const newUrl = Url.format({
          protocol: this._window.location.protocol,
          hostname: newHost,
          port: this._window.location.port,
          pathname: this._window.location.pathname
        });
        this._notificationService.addNotification({
          id: 'server-error',
          content: 'You should use this domain: ' + newUrl
        });
        return true;
    }
    return false;
  }

  private isAuthorizationNeeded(): boolean {
    return !(this._state.is(bcStates.login.url) || this._state.is(bcStates.logout.url));
  }

  private isIdentityValidationError(error: HttpErrorResponse): boolean {
    return error.error && error.error[0] && error.error[0].code === bcEnums.loginErrorCodes.IDENTITY_VALIDATION_FAILED;
  }
}
